import React, { useState, useEffect, useCallback } from 'react';
import { 
  FileText, Plus, DollarSign, Users, Calendar, Send as SendIcon,
  Trash2, ChevronDown, ChevronUp, CheckCircle2, Clock, XCircle,
  Edit, Eye, FileSpreadsheet, Receipt
} from 'lucide-react';
import { Header } from '../components/Header';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { EmptyState } from '../components/EmptyState';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const statusCfg = {
  draft: { label: 'Borrador', color: 'bg-muted text-foreground/80 border-border', icon: Edit },
  sent: { label: 'Enviada', color: 'bg-brand-green/10 text-brand-deep dark:text-brand-green border-brand-green/30', icon: SendIcon },
  paid: { label: 'Pagada', color: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30', icon: CheckCircle2 },
  overdue: { label: 'Vencida', color: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30', icon: Clock },
  cancelled: { label: 'Cancelada', color: 'bg-gray-500/10 text-gray-400 border-gray-500/30', icon: XCircle },
};

const Invoices = () => {
  const { isAdmin } = useAuth();
  const { customers, products } = useApp();
  const [invoices, setInvoices] = useState([]);
  const [tab, setTab] = useState('invoices');
  const [showCreate, setShowCreate] = useState(false);
  const [expandedInv, setExpandedInv] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const [form, setForm] = useState({
    customer_id: '', invoice_type: 'invoice', currency: 'usd', notes: '', due_date: '',
    items: [{ description: '', quantity: 1, unit_price: 0 }]
  });

  const fetchInvoices = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (typeFilter !== 'all') params.append('invoice_type', typeFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      const res = await axios.get(`${API}/invoices?${params}`);
      setInvoices(res.data);
    } catch (err) { console.warn('Invoice load error:', err?.message); toast.error('Error al cargar facturas'); }
  }, [typeFilter, statusFilter]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const addItem = () => setForm(prev => ({ ...prev, items: [...prev.items, { description: '', quantity: 1, unit_price: 0 }] }));
  const removeItem = (idx) => setForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
  const updateItem = (idx, field, val) => {
    setForm(prev => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: field === 'description' ? val : Number(val) };
      return { ...prev, items };
    });
  };

  const subtotal = form.items.reduce((sum, it) => sum + (it.quantity * it.unit_price), 0);
  const tax = subtotal * 0.16;
  const total = subtotal + tax;

  const handleCreate = async () => {
    if (!form.customer_id || form.items.length === 0 || !form.items[0].description) return;
    try {
      await axios.post(`${API}/invoices`, {
        customer_id: form.customer_id,
        items: form.items,
        currency: form.currency,
        notes: form.notes,
        due_date: form.due_date || null,
        invoice_type: form.invoice_type
      });
      toast.success(form.invoice_type === 'invoice' ? 'Factura creada' : 'Cotización creada');
      setShowCreate(false);
      setForm({ customer_id: '', invoice_type: 'invoice', currency: 'usd', notes: '', due_date: '', items: [{ description: '', quantity: 1, unit_price: 0 }] });
      fetchInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.put(`${API}/invoices/${id}/status?status=${newStatus}`);
      toast.success('Estado actualizado');
      fetchInvoices();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este documento?')) return;
    try { await axios.delete(`${API}/invoices/${id}`); toast.success('Eliminado'); fetchInvoices(); }
    catch { toast.error('Error al eliminar'); }
  };

  const addProductAsItem = (productId) => {
    const prod = products.find(p => p.id === productId);
    if (!prod) return;
    setForm(prev => ({
      ...prev,
      items: [...prev.items.filter(it => it.description), { description: prod.name, quantity: 1, unit_price: prod.price || 0 }]
    }));
  };

  const fmt = (n) => `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

  const invoiceCount = invoices.filter(i => i.invoice_type === 'invoice').length;
  const quoteCount = invoices.filter(i => i.invoice_type === 'quote').length;
  const paidTotal = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + i.total, 0);
  const pendingTotal = invoices.filter(i => i.status === 'sent' || i.status === 'draft').reduce((s, i) => s + i.total, 0);

  return (
    <div data-testid="invoices-page" className="min-h-screen">
      <Header title="Facturación" subtitle="Facturas y cotizaciones">
        <Button data-testid="new-invoice-btn" className="gap-2 bg-primary hover:opacity-90" onClick={() => setShowCreate(true)}>
          <Plus className="w-4 h-4" /> Nuevo Documento
        </Button>
      </Header>

      <div className="p-6 space-y-6 max-w-6xl">
        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="glass-card">
            <CardContent className="p-4 text-center">
              <FileText className="w-6 h-6 mx-auto mb-2 text-brand-deep dark:text-brand-green" />
              <p className="text-xs text-muted-foreground">Facturas</p>
              <p className="font-bold text-lg">{invoiceCount}</p>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-4 text-center">
              <Receipt className="w-6 h-6 mx-auto mb-2 text-brand-deep dark:text-brand-deep dark:text-brand-green" />
              <p className="text-xs text-muted-foreground">Cotizaciones</p>
              <p className="font-bold text-lg">{quoteCount}</p>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-4 text-center">
              <CheckCircle2 className="w-6 h-6 mx-auto mb-2 text-emerald-700 dark:text-emerald-400" />
              <p className="text-xs text-muted-foreground">Cobrado</p>
              <p className="font-bold text-lg text-emerald-700 dark:text-emerald-400">{fmt(paidTotal)}</p>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardContent className="p-4 text-center">
              <Clock className="w-6 h-6 mx-auto mb-2 text-amber-700 dark:text-amber-400" />
              <p className="text-xs text-muted-foreground">Pendiente</p>
              <p className="font-bold text-lg text-amber-700 dark:text-amber-400">{fmt(pendingTotal)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-40" data-testid="type-filter"><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="invoice">Facturas</SelectItem>
              <SelectItem value="quote">Cotizaciones</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="inv-status-filter"><SelectValue placeholder="Estado" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="draft">Borrador</SelectItem>
              <SelectItem value="sent">Enviada</SelectItem>
              <SelectItem value="paid">Pagada</SelectItem>
              <SelectItem value="overdue">Vencida</SelectItem>
              <SelectItem value="cancelled">Cancelada</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground">{invoices.length} documentos</span>
        </div>

        {/* Invoice List */}
        <AnimatePresence>
          {invoices.map(inv => {
            const cfg = statusCfg[inv.status] || statusCfg.draft;
            const StatusIcon = cfg.icon;
            const isExpanded = expandedInv === inv.id;

            return (
              <motion.div key={inv.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <Card data-testid={`invoice-${inv.id}`} className="glass-card overflow-hidden">
                  <CardContent className="p-0">
                    <div className="p-4 cursor-pointer hover:bg-muted/20 transition-colors" onClick={() => setExpandedInv(isExpanded ? null : inv.id)}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", cfg.color)}>
                            <StatusIcon className="w-5 h-5" />
                          </div>
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className="font-mono font-bold text-sm">{inv.invoice_number}</span>
                              <Badge variant="outline" className={cn("text-xs", cfg.color)}>{cfg.label}</Badge>
                              <Badge variant="outline" className="text-xs">{inv.invoice_type === 'invoice' ? 'Factura' : 'Cotización'}</Badge>
                            </div>
                            <p className="text-xs text-muted-foreground truncate">{inv.customer_name} &middot; {new Date(inv.created_at).toLocaleDateString('es')}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                          <p className="font-bold text-base sm:text-lg whitespace-nowrap">{fmt(inv.total)}</p>
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </div>
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="border-t border-border p-4 bg-muted/10 space-y-4">
                        {/* Items table */}
                        <table className="w-full text-sm">
                          <thead><tr className="text-xs text-muted-foreground border-b border-border">
                            <th className="text-left pb-2">Descripción</th>
                            <th className="text-right pb-2">Cant.</th>
                            <th className="text-right pb-2">Precio</th>
                            <th className="text-right pb-2">Total</th>
                          </tr></thead>
                          <tbody>
                            {inv.items?.map((item, idx) => (
                              <tr key={`inv-row-${idx}`} className="border-b border-border/50">
                                <td className="py-2">{item.description}</td>
                                <td className="py-2 text-right">{item.quantity}</td>
                                <td className="py-2 text-right">{fmt(item.unit_price)}</td>
                                <td className="py-2 text-right font-medium">{fmt(item.total)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr><td colSpan="3" className="text-right pt-2 text-muted-foreground">Subtotal</td><td className="text-right pt-2">{fmt(inv.subtotal)}</td></tr>
                            <tr><td colSpan="3" className="text-right text-muted-foreground">IVA (16%)</td><td className="text-right">{fmt(inv.tax)}</td></tr>
                            <tr className="font-bold text-base"><td colSpan="3" className="text-right pt-1">Total</td><td className="text-right pt-1">{fmt(inv.total)}</td></tr>
                          </tfoot>
                        </table>
                        {inv.notes && <p className="text-sm text-muted-foreground">Notas: {inv.notes}</p>}
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Calendar className="w-3 h-3" /> Vencimiento: {inv.due_date}
                          {inv.paid_at && <> &middot; Pagada: {new Date(inv.paid_at).toLocaleDateString('es')}</>}
                        </div>
                        {/* Actions */}
                        <div className="flex gap-2 pt-2">
                          {inv.status === 'draft' && (
                            <>
                              <Button size="sm" className="bg-brand-green hover:bg-brand-green gap-1" onClick={(e) => { e.stopPropagation(); handleStatusChange(inv.id, 'sent'); }}>
                                <SendIcon className="w-3 h-3" /> Enviar
                              </Button>
                              <Button size="sm" variant="destructive" className="gap-1" onClick={(e) => { e.stopPropagation(); handleDelete(inv.id); }}>
                                <Trash2 className="w-3 h-3" /> Eliminar
                              </Button>
                            </>
                          )}
                          {inv.status === 'sent' && (
                            <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 gap-1" onClick={(e) => { e.stopPropagation(); handleStatusChange(inv.id, 'paid'); }}>
                              <CheckCircle2 className="w-3 h-3" /> Marcar Pagada
                            </Button>
                          )}
                          {['draft', 'sent'].includes(inv.status) && (
                            <Button size="sm" variant="outline" className="text-red-600 dark:text-red-400 border-red-500/30 gap-1" onClick={(e) => { e.stopPropagation(); handleStatusChange(inv.id, 'cancelled'); }}>
                              <XCircle className="w-3 h-3" /> Cancelar
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </AnimatePresence>

        {invoices.length === 0 && (
          <Card className="glass-card border-dashed">
            <CardContent className="py-12 text-center">
              <EmptyState
                mood="empty"
                title="Aún no hay documentos"
                description="Cuando emitas facturas o cotizaciones aparecerán aquí con su estado de pago."
                action={(
                  <Button variant="outline" className="gap-2" onClick={() => setShowCreate(true)}>
                    <Plus className="w-4 h-4" /> Crear primera factura
                  </Button>
                )}
                testId="invoices-empty"
              />
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create Invoice Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nuevo Documento</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipo</Label>
                <Select value={form.invoice_type} onValueChange={v => setForm({...form, invoice_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="invoice">Factura</SelectItem>
                    <SelectItem value="quote">Cotización</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Moneda</Label>
                <Select value={form.currency} onValueChange={v => setForm({...form, currency: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="usd">USD</SelectItem>
                    <SelectItem value="eur">EUR</SelectItem>
                    <SelectItem value="mxn">MXN</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Cliente *</Label>
              <Select value={form.customer_id} onValueChange={v => setForm({...form, customer_id: v})}>
                <SelectTrigger data-testid="invoice-customer"><SelectValue placeholder="Seleccionar cliente" /></SelectTrigger>
                <SelectContent>
                  {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Quick add from products */}
            {products.length > 0 && (
              <div>
                <Label className="text-xs text-muted-foreground">Agregar producto rápido</Label>
                <Select onValueChange={addProductAsItem}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Seleccionar producto..." /></SelectTrigger>
                  <SelectContent>
                    {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name} - {fmt(p.price || 0)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Items */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Ítems</Label>
                <Button type="button" variant="ghost" size="sm" className="text-xs gap-1" onClick={addItem}><Plus className="w-3 h-3" /> Agregar</Button>
              </div>
              {form.items.map((item, idx) => (
                <div key={`inv-item-${idx}`} className="flex gap-2 items-start">
                  <Input className="flex-1" placeholder="Descripción" value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} />
                  <Input className="w-16" type="number" placeholder="Cant" value={item.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)} min="1" />
                  <Input className="w-24" type="number" placeholder="Precio" value={item.unit_price} onChange={e => updateItem(idx, 'unit_price', e.target.value)} min="0" step="0.01" />
                  {form.items.length > 1 && (
                    <Button variant="ghost" size="icon" className="h-10 w-10 text-destructive" onClick={() => removeItem(idx)}><Trash2 className="w-3 h-3" /></Button>
                  )}
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="p-3 rounded-lg bg-muted/50 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal:</span><span>{fmt(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">IVA (16%):</span><span>{fmt(tax)}</span></div>
              <div className="flex justify-between font-bold border-t border-border pt-1 mt-1"><span>Total:</span><span>{fmt(total)}</span></div>
            </div>

            <div>
              <Label>Fecha de vencimiento</Label>
              <Input type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} />
            </div>
            <div>
              <Label>Notas</Label>
              <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Notas adicionales..." rows={2} />
            </div>

            <Button data-testid="create-invoice-btn" className="w-full bg-primary" onClick={handleCreate}
              disabled={!form.customer_id || !form.items[0]?.description}>
              <FileText className="w-4 h-4 mr-2" /> {form.invoice_type === 'invoice' ? 'Crear Factura' : 'Crear Cotización'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Invoices;
